import { useEffect, useState, type ReactElement } from "react";
import { C, FONT_MONO, hexA, initialOf, statusColor, statusLabel } from "@/theme";
import { colorOf, friendState, roleOf, useApp, userById } from "@/state/store";

/** "MEMBRO DESDE AGO. DE 2026" a partir da data real de criacao da conta. */
function memberSince(createdAt: number): string {
  return new Date(createdAt)
    .toLocaleDateString("pt-BR", { month: "short", year: "numeric" })
    .toUpperCase();
}

/** Foto do membro; sem foto (ou se ela falhar) cai na inicial colorida do desenho. */
function Avatar({
  url,
  name,
  color,
}: {
  url: string | null;
  name: string;
  color: string;
}): ReactElement {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [url]);

  const frame = {
    width: 68,
    height: 68,
    borderRadius: "50%",
    border: "4px solid #1d1d1d",
    background: hexA(color, 0.18),
  } as const;

  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setBroken(true)}
        style={{ ...frame, display: "block", objectFit: "cover", boxSizing: "border-box" }}
      />
    );
  }
  return (
    <span
      style={{
        ...frame,
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
        fontSize: 26,
        fontWeight: 700,
        color,
      }}
    >
      {initialOf(name)}
    </span>
  );
}

export function ProfileModal() {
  const { state, actions } = useApp();
  const user = state.profile ? userById(state, state.profile) : undefined;
  const open = Boolean(user);
  const { closeProfile } = actions;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeProfile();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeProfile, open]);

  if (!user) return null;

  const color = colorOf(state, user.id);
  const role = roleOf(state, user.id);
  /*
    TODOS os cargos, não só o mais alto.

    O resto da tela mostra o cargo mais graduado — é ele que dá a cor do nome e
    agrupa o painel de membros, como no Discord. Só que ele era o ÚNICO lugar
    onde cargo aparecia, e a conta disso é cruel: dar a alguém um cargo abaixo
    do que ela já tem grava no banco, viaja em tempo real, chega nas duas telas
    — e não muda um pixel. De fora, é idêntico a não ter funcionado.

    Na prática era o caso mais comum, não o raro: quem administra costuma já
    carregar o cargo do topo, então quase todo cargo dado fica embaixo dele.

    Aqui a lista inteira aparece, na ordem do servidor (mais graduado
    primeiro). Como sai direto do estado, ligar e desligar um cargo aparece na
    hora, para quem deu e para quem recebeu.
  */
  const cargos = (user.roleIds.length > 0 ? user.roleIds : role ? [role.id] : [])
    .map(id => state.roles.find(item => item.id === id))
    .filter((item): item is NonNullable<typeof item> => item !== undefined);
  const notMe = user.id !== state.me?.id;
  const bio = user.bio.trim();
  const relation = friendState(state, user.id);
  // Conversa que já existe continua aberta mesmo sem amizade — o servidor usa
  // a mesma regra, e o botão não pode prometer menos do que ele permite.
  const hasConversation = state.conversations.includes(user.id);

  return (
    <div
      onClick={closeProfile}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "grid",
        placeItems: "center",
        // O padding nao muda nada no desktop (o cartao tem 340px fixos); ele so
        // impede que o modal encoste nas bordas quando o MobileApp o reaproveita.
        padding: 20,
        background: "rgba(0,0,0,.62)",
        animation: "fadein 150ms ease",
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: 340,
          maxWidth: "100%",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          overflow: "hidden",
          background: "#1d1d1d",
          animation: "pop 200ms ease",
        }}
      >
        <div style={{ height: 74, background: hexA(color, 0.22) }} />
        <div style={{ padding: "0 20px 20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              marginTop: -30,
            }}
          >
            <Avatar url={user.avatarUrl} name={user.name} color={color} />
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 999,
                border: `1px solid ${C.lineStrong}`,
                background: C.panel,
                fontSize: 11.5,
                color: C.textSoft,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: statusColor(user.status),
                }}
              />
              {statusLabel(user.status)}
            </span>
          </div>
          <div style={{ marginTop: 12, fontSize: 20, fontWeight: 700 }}>{user.name}</div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: C.textFaint, marginTop: 2 }}>
            {`@${user.handle}`}
          </div>
          {user.customStatus && (
            <div style={{ marginTop: 6, fontSize: 13, color: C.textBody }}>{user.customStatus}</div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
            {(cargos.length > 0 ? cargos : [null]).map((cargo, indice) => (
              <span
                key={cargo?.id ?? `sem-cargo-${indice}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 7,
                  border: `1px solid ${C.lineStrong}`,
                  background: C.panel,
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: C.textBody,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 3,
                    background: cargo?.color ?? state.settings.accent,
                  }}
                />
                {cargo?.name ?? ""}
              </span>
            ))}
            {user.serverMuted && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 7,
                  border: `1px solid ${hexA(C.red, 0.3)}`,
                  background: hexA(C.red, 0.1),
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: C.red,
                }}
              >
                Silenciado
              </span>
            )}
            {user.serverDeafened && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 7,
                  border: `1px solid ${hexA(C.amber, 0.32)}`,
                  background: hexA(C.amber, 0.1),
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: C.amber,
                }}
              >
                Áudio desativado
              </span>
            )}
          </div>
          {bio && (
            <div
              style={{
                marginTop: 14,
                fontSize: 13,
                lineHeight: 1.55,
                color: C.textSoft,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {bio}
            </div>
          )}
          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px solid #292929",
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: "0.08em",
              color: C.textLabel,
            }}
          >
            {`MEMBRO DESDE ${memberSince(user.createdAt)}`}
          </div>
          {/*
            O botão segue a relação. Antes era sempre "Enviar mensagem", o que
            agora abriria uma conversa que o servidor recusa — a promessa da
            tela precisa bater com o que o servidor deixa fazer.
          */}
          <button
            type="button"
            className="hov-bright-108"
            disabled={relation === "enviada"}
            onClick={() => {
              if (!notMe) {
                // O editor abre sozinho: dois modais empilhados so atrapalhariam.
                actions.closeProfile();
                actions.openProfileEditor();
                return;
              }
              if (relation === "amigo" || !state.friendsAvailable || hasConversation) {
                actions.messageUser(user.id);
                return;
              }
              if (relation === "recebida") {
                actions.acceptFriend(user.id);
                return;
              }
              actions.addFriend({ userId: user.id });
            }}
            style={{
              width: "100%",
              marginTop: 16,
              padding: "11px 0",
              border: 0,
              borderRadius: 10,
              cursor: relation === "enviada" ? "default" : "pointer",
              background: "var(--ac, #1F4FD8)",
              color: C.ink,
              fontSize: 13.5,
              fontWeight: 700,
              opacity: relation === "enviada" ? 0.55 : 1,
            }}
          >
            {!notMe
              ? "Editar perfil"
              : relation === "amigo" || !state.friendsAvailable || hasConversation
                ? "Enviar mensagem"
                : relation === "recebida"
                  ? "Aceitar solicitação"
                  : relation === "enviada"
                    ? "Solicitação enviada"
                    : "Adicionar amigo"}
          </button>

          {notMe && relation === "amigo" ? (
            <button
              type="button"
              onClick={() => actions.removeFriend(user.id)}
              style={{
                width: "100%",
                marginTop: 8,
                padding: "8px 0",
                border: 0,
                borderRadius: 9,
                cursor: "pointer",
                background: "transparent",
                color: C.textMuted,
                fontSize: 12,
              }}
            >
              Desfazer amizade
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
